// User feed batch selection functionality

interface SelectionState {
    selected: Set<string>;
    isSelectionMode: boolean;
}

const state: SelectionState = {
    selected: new Set(),
    isSelectionMode: false,
};

function initBatchSelection() {
    const feedContainer = document.querySelector('.pano-cards-grid');
    if (!feedContainer) return;

    // Add selection UI
    addSelectionBar();
    addCheckboxesToCards();
}

function addSelectionBar() {
    const feedHeader = document.querySelector('.feed-header-container');
    if (!feedHeader) return;

    const selectionBar = document.createElement('div');
    selectionBar.id = 'selection-bar';
    selectionBar.className =
        'hidden fixed top-0 left-0 right-0 bg-blue-600 text-white py-3 px-4 z-50 shadow-lg';
    selectionBar.innerHTML = `
        <div class="container mx-auto flex items-center justify-between">
            <span id="selection-count" class="font-medium">0 selected</span>
            <div class="flex items-center space-x-3">
                <button id="select-all-btn" class="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded text-sm">
                    Select All
                </button>
                <button id="deselect-all-btn" class="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded text-sm">
                    Deselect All
                </button>
                <button id="edit-selected-btn" class="px-4 py-1 bg-white text-blue-600 hover:bg-gray-100 rounded font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                    Edit Selected
                </button>
                <button id="cancel-selection-btn" class="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded text-sm">
                    Cancel
                </button>
            </div>
        </div>
    `;

    document.body.prepend(selectionBar);

    // Event listeners
    document
        .getElementById('select-all-btn')
        ?.addEventListener('click', selectAll);
    document
        .getElementById('deselect-all-btn')
        ?.addEventListener('click', deselectAll);
    document
        .getElementById('edit-selected-btn')
        ?.addEventListener('click', editSelected);
    document
        .getElementById('cancel-selection-btn')
        ?.addEventListener('click', exitSelectionMode);
}

function addCheckboxesToCards() {
    const panoCards = document.querySelectorAll('[data-geohash]');

    panoCards.forEach((card) => {
        const geohash = card.getAttribute('data-geohash');
        if (!geohash) return;

        // Add checkbox overlay
        const checkboxContainer = document.createElement('div');
        checkboxContainer.className =
            'checkbox-container absolute top-2 left-2 z-10 hidden';
        checkboxContainer.innerHTML = `
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox"
                       class="pano-checkbox w-5 h-5 rounded border-2 border-white shadow-lg cursor-pointer"
                       data-geohash="${geohash}"
                       style="accent-color: #2563eb;">
            </label>
        `;

        // Add checkbox to the card (which already has relative positioning)
        card.appendChild(checkboxContainer);

        // Checkbox change listener
        const checkbox = checkboxContainer.querySelector(
            'input'
        ) as HTMLInputElement;
        checkbox?.addEventListener('change', (e) => {
            e.stopPropagation();
            handleCheckboxChange(geohash, checkbox.checked, card as HTMLElement);
        });
    });

    // Add "Enable Selection" button to header
    const feedHeader = document.querySelector('.feed-header-container');
    if (feedHeader && panoCards.length > 0) {
        const enableBtn = document.createElement('button');
        enableBtn.id = 'enable-selection-btn';
        enableBtn.className =
            'mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium';
        enableBtn.textContent = 'Select Panoramas';
        enableBtn.addEventListener('click', enterSelectionMode);
        feedHeader.appendChild(enableBtn);
    }
}

function enterSelectionMode() {
    state.isSelectionMode = true;
    document.getElementById('selection-bar')?.classList.remove('hidden');
    document
        .getElementById('enable-selection-btn')
        ?.classList.add('hidden');

    // Show all checkboxes
    document.querySelectorAll('.checkbox-container').forEach((el) => {
        el.classList.remove('hidden');
    });

    updateSelectionUI();
}

function exitSelectionMode() {
    state.isSelectionMode = false;
    state.selected.clear();

    document.getElementById('selection-bar')?.classList.add('hidden');
    document
        .getElementById('enable-selection-btn')
        ?.classList.remove('hidden');

    // Hide checkboxes and uncheck all
    document.querySelectorAll('.checkbox-container').forEach((el) => {
        el.classList.add('hidden');
    });

    // Remove selection background from all cards
    document.querySelectorAll('[data-geohash]').forEach((card) => {
        card.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
    });

    document
        .querySelectorAll('.pano-checkbox')
        .forEach((checkbox: Element) => {
            (checkbox as HTMLInputElement).checked = false;
        });

    updateSelectionUI();
}

function handleCheckboxChange(
    geohash: string,
    checked: boolean,
    card: HTMLElement
) {
    if (checked) {
        state.selected.add(geohash);
        card.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
    } else {
        state.selected.delete(geohash);
        card.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
    }
    updateSelectionUI();
}

function selectAll() {
    document.querySelectorAll('[data-geohash]').forEach((card) => {
        const geohash = card.getAttribute('data-geohash');
        if (!geohash) return;

        state.selected.add(geohash);
        card.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');

        const checkbox = card.querySelector(
            '.pano-checkbox'
        ) as HTMLInputElement;
        if (checkbox) checkbox.checked = true;
    });
    updateSelectionUI();
}

function deselectAll() {
    document.querySelectorAll('[data-geohash]').forEach((card) => {
        const geohash = card.getAttribute('data-geohash');
        if (!geohash) return;

        state.selected.delete(geohash);
        card.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');

        const checkbox = card.querySelector(
            '.pano-checkbox'
        ) as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
    });
    updateSelectionUI();
}

function editSelected() {
    if (state.selected.size === 0) return;

    const ids = Array.from(state.selected).join(',');
    window.location.href = `/pano/edit?ids=${ids}`;
}

function updateSelectionUI() {
    const countEl = document.getElementById('selection-count');
    const editBtn = document.getElementById(
        'edit-selected-btn'
    ) as HTMLButtonElement;

    if (countEl) {
        countEl.textContent = `${state.selected.size} selected`;
    }

    if (editBtn) {
        editBtn.disabled = state.selected.size === 0;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBatchSelection);
} else {
    initBatchSelection();
}
